import React, { useRef, useState, useMemo, useEffect } from "react";
import cl from "classnames";
import PropTypes from "prop-types";
import classes from "./styles.module.scss";
import formatOptionLabel from "./formatOptionLabel";
import ClearButton from "./ClearButton";
import fixDropDownPositionMobile from "./fixMobileMenuPosition";
import useFixMobileMenuPositionOnScroll from "./useFixMobileMenuPositionOnScroll";
import { components, AsyncCreatable, Async } from "react-select";
import isSSR from "venuescanner-js/services/isSSR";

const Input =
  (extraProps = {}) =>
  (props) =>
    <components.Input {...props} {...extraProps} />;
const formatCreateLabel = (value) => value;

const AsyncSelectField = React.forwardRef(
  (
    {
      value,
      onChange,
      containerClassName,
      className,
      getResults,
      clearButton = false,
      inputStyles = false,
      placeholder,
      inputProps = {},
      absolute = false,
      defaultOptions = true,
      allowCustom = false,
      required = false,
      ...props
    },
    ref
  ) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    useFixMobileMenuPositionOnScroll(menuIsOpen);
    const containerRef = useRef(null);
    const Component = allowCustom ? AsyncCreatable : Async;
    const customComponents = useMemo(
      () => ({ Input: Input(inputProps) }),
      [inputProps]
    );
    useEffect(() => {
      if (defaultOptions && !value && menuIsOpen && !isSSR()) {
        setTimeout(() => {
          const ip = containerRef?.current?.querySelector("input");
          if (ip) {
            ip.focus();
          }
        });
      }
    }, [defaultOptions, value, menuIsOpen]);
    const controlStyles = {};
    const menuStyles = { zIndex: 9999 };
    // if (props.children)
    controlStyles.border = "none";
    const styles = useMemo(
      () => ({
        placeholder: (base) => ({
          ...base,
          color: "#ccc",
          fontSize: "1rem",
        }),
        menuPortal: (base) => ({ ...base, zIndex: 2147483002 }),
        singleValue: (base) => ({
          ...base,
          color: menuIsOpen ? "#ccc" : "#333",
        }),
        control: (base) => ({
          ...base,
          ...controlStyles,
        }),
        menu: (base) => ({
          ...base,
          ...menuStyles,
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected ? "#471dd1" : "#ffffff",
          color: state.isSelected ? "#ffffff" : "#545454",
        }),
      }),
      [controlStyles, menuIsOpen]
    );
    return (
      <div
        ref={containerRef}
        className={cl(classes.SelectFieldContainer, containerClassName, {
          [classes.SelectFieldContainerPrefixed]: true /*! !props.children */,
          [classes.SelectFieldContainerInputStyled]: inputStyles,
        })}
      >
        {/* <Suspense fallback={<vs-loader />}> */}
        {props.children && (
          <span className={cl(classes.PrefixedElement)}>{props.children}</span>
        )}
        <Component
          components={customComponents}
          value={menuIsOpen ? { label: "Start typing..." } : value}
          placeholder={menuIsOpen ? "Start typing..." : placeholder}
          onChange={onChange}
          className={cl(classes.SelectField, className, {
            [classes.Select_override]: inputStyles,
          })}
          classNamePrefix="SelectField"
          formatOptionLabel={formatOptionLabel}
          menuPortalTarget={absolute || isSSR() ? null : document.body}
          styles={styles}
          menuPosition={absolute ? "absolute" : "fixed"}
          // menuShouldBlockScroll={true}
          menuPlacement={absolute ? undefined : "auto"}
          required={required}
          ref={ref}
          cacheOptions={false}
          defaultOptions={defaultOptions}
          loadOptions={getResults}
          onInputChange={() => {
            if (!absolute) fixDropDownPositionMobile();
          }}
          onMenuOpen={() => {
            setMenuIsOpen(true);
            if (!absolute) fixDropDownPositionMobile();
          }}
          onMenuClose={() => {
            setMenuIsOpen(false);
          }}
          menuIsOpen={menuIsOpen}
          formatCreateLabel={formatCreateLabel}
          createOptionPosition="first"
        ></Component>
        {!!value && clearButton && <ClearButton onClick={() => onChange("")} />}
        {/* </Suspense> */}
      </div>
    );
  }
);

AsyncSelectField.displayName = AsyncSelectField;

AsyncSelectField.propTypes = {
  value: PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  absolute: PropTypes.bool,
  containerClassName: PropTypes.string,
  getResults: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  inputProps: PropTypes.object,
  allowCustom: PropTypes.bool,
  required: PropTypes.bool,
  clearButton: PropTypes.bool,
  defaultOptions: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  children: PropTypes.node,
};

export default AsyncSelectField;
